import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import { ReminderModal, ReminderStatus, ReminderWarningIcon } from '~/components/Reminders';
import { enumValue, formatDate } from '~/util';
import { createdByUserName, getGapNotePCM } from '~/util/reminderUtils';

// Type assertion for TypeScript 5.x compatibility with react-router-dom v5
const LinkComponent = Link as any;

interface ReminderProps {
    reminder: ReminderType;
    patient: null | PatientType;
    enableOpenModal?: boolean;
    showPatientLink?: boolean;
    isPCMView?: boolean;
}

export function Reminder({
    reminder,
    patient,
    enableOpenModal = false,
    showPatientLink = false,
    isPCMView = false,
}: ReminderProps) {
    const createdByUser = React.useMemo(() => {
        return createdByUserName(reminder);
    }, [reminder]);

    const isReminderOverdue = React.useMemo(() => {
        if (reminder.status !== enumValue(['task', 'status', 'Not Started'])) return false;
        const today = moment().utc(true).hour(0).minute(0).second(0);
        return moment(reminder.due_at).isBefore(today, 'day');
    }, [reminder.due_at, reminder.status]);

    function getPatientLink() {
        return (
            <LinkComponent to={`/patients/${reminder.patient.id}`} className="fw-bold">
                {reminder.patient?.first_name} {reminder.patient?.last_name}
            </LinkComponent>
        );
    }

    function getHeaderText() {
        if (!enableOpenModal && !showPatientLink) {
            return <span>reminder.</span>;
        }
        if (showPatientLink) {
            if (reminder.type === enumValue(['note', 'type', 'Patient Note'])) {
                return <span>reminder for {getPatientLink()}.</span>;
            }
            return (
                <span>
                    reminder on {getGapNotePCM(reminder, patient)} for {getPatientLink()}.
                </span>
            );
        }
        if (reminder.type === enumValue(['note', 'type', 'Patient Note'])) {
            return <span>reminder on this patient.</span>;
        }
        return (
            <>
                <span>reminder on </span>
                {getGapNotePCM(reminder, patient)}.
            </>
        );
    }

    return (
        <div className="card border-info mb-2 reminder-card">
            <div className="card-header card-header-ignore-margin">
                <div className="row">
                    <div className="col-6">
                        <div className="pull-left">
                            <i
                                className={`fa-regular ${
                                    reminder?.timely_action ? 'fa-clock' : 'fa-bell'
                                } me-1`}
                            />
                            {createdByUser && (
                                <span className="fw-bold">{createdByUser}&apos;s </span>
                            )}
                            {reminder?.timely_action && (
                                <span className="fw-bold">Timely Action </span>
                            )}
                            {getHeaderText()}
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="pull-right">
                            <ReminderModal
                                reminder={reminder}
                                patient={patient?.id ? patient : reminder.patient}
                                className="me-3"
                            />
                            <span className="badge badge-outline-secondary audit-report-year">
                                {reminder.report_year}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <div className="row align-items-center">
                    <div className={`${isPCMView ? 'col-5' : 'col-7'}`}>{reminder.description}</div>
                    <div
                        className={`col text-end${isReminderOverdue ? ' text-danger' : ''}`}
                        title={isReminderOverdue ? 'This reminder is overdue' : undefined}
                    >
                        <span className="fw-bold">Due Date:</span> {formatDate(reminder.due_at)}
                        <ReminderWarningIcon reminder={reminder} />
                    </div>
                    <div className="col text-end">
                        <ReminderStatus reminder={reminder} />
                    </div>
                </div>
            </div>
        </div>
    );
}
